import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import fiscalPrinterService from '../../../services/fiscalPrinterService';

const FiscalPrinterTab = () => {
  const [printerStatus, setPrinterStatus] = useState({
    connected: false,
    supported: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);

  // Protokół komunikacji: 'escpos' lub 'xml' (domyślnie XML dla Novitus Deon Online)
  const [protocol, setProtocol] = useState(() => {
    return localStorage.getItem('fiscal_printer_protocol') || 'xml';
  });

  // Dodaj log
  const addLog = (message, type = 'info') => {
    setLogs(prev => [{
      time: new Date().toLocaleTimeString(),
      message,
      type
    }, ...prev.slice(0, 49)]); // Zachowaj ostatnie 50 logów
  };

  // Sprawdź wsparcie dla Web Serial API
  useEffect(() => {
    const supported = fiscalPrinterService.isSupported();
    setPrinterStatus(prev => ({ ...prev, supported }));

    if (!supported) {
      setError('Web Serial API nie jest wspierane. Użyj Chrome 89+ lub Edge 89+');
      addLog('Web Serial API nie jest wspierane w tej przeglądarce', 'error');
    } else {
      addLog('Web Serial API dostępne', 'success');
    }

    // Załaduj zapisany protokół (domyślnie XML dla Novitus Deon Online)
    const savedProtocol = localStorage.getItem('fiscal_printer_protocol') || 'xml';
    fiscalPrinterService.setProtocol(savedProtocol);
    addLog(`Protokół komunikacji: ${savedProtocol === 'xml' ? 'XML' : 'ESC/POS'}`, 'info');
  }, []);

  // Zmiana protokołu komunikacji
  const handleProtocolChange = (newProtocol) => {
    // ESC/POS NIE drukuje paragonow fiskalnych (Novitus Deon fiskalizuje
    // wylacznie przez XML) - przelaczenie wymaga swiadomego potwierdzenia.
    if (newProtocol === 'escpos') {
      const zgoda = window.confirm(
        'UWAGA: protokol ESC/POS NIE obsluguje paragonow fiskalnych!\n\n'
        + 'Na ESC/POS dzialaja tylko dokumenty niefiskalne i raporty.\n'
        + 'Sprzedaz z paragonem fiskalnym wymaga protokolu XML - proba druku\n'
        + 'paragonu na ESC/POS zostanie ODRZUCONA, a transakcja pozostanie\n'
        + 'niezafiskalizowana.\n\n'
        + 'Czy na pewno przelaczyc na ESC/POS?'
      );
      if (!zgoda) return;
      addLog('ESC/POS: paragony fiskalne NIE beda drukowane (wymagany XML)', 'error');
    }
    setProtocol(newProtocol);
    localStorage.setItem('fiscal_printer_protocol', newProtocol);
    fiscalPrinterService.setProtocol(newProtocol);
    addLog(`Zmieniono protokół na: ${newProtocol === 'xml' ? 'XML' : 'ESC/POS'}`, 'success');
  };

  // Połącz z drukarką
  const connectPrinter = async () => {
    try {
      setLoading(true);
      setError(null);
      addLog('Łączenie z drukarką...');

      await fiscalPrinterService.connect();

      setPrinterStatus(prev => ({ ...prev, connected: true }));
      addLog('Połączono z drukarką', 'success');

      // Pobierz status
      const status = await fiscalPrinterService.getStatus();
      addLog(`Status drukarki: ${JSON.stringify(status)}`, 'info');

    } catch (err) {
      setError(`Błąd połączenia: ${err.message}`);
      addLog(`Błąd połączenia: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Rozłącz drukarkę
  const disconnectPrinter = async () => {
    try {
      setLoading(true);
      await fiscalPrinterService.disconnect();
      setPrinterStatus(prev => ({ ...prev, connected: false }));
      addLog('Rozłączono z drukarką', 'info');
    } catch (err) {
      addLog(`Błąd rozłączania: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Test połączenia
  const testConnection = async () => {
    try {
      setLoading(true);
      addLog('Testowanie połączenia...');

      const status = await fiscalPrinterService.getStatus();

      if (status.connected) {
        addLog('Test połączenia: OK', 'success');
        toast.success('Test połączenia pomyślny!');
      } else {
        addLog('Test połączenia: BŁĄD', 'error');
        toast.error('Test połączenia nieudany');
      }
    } catch (err) {
      addLog(`Błąd testu: ${err.message}`, 'error');
      toast.error(`Błąd: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Otwórz szufladę
  const openDrawer = async () => {
    try {
      setLoading(true);
      addLog('Otwieram szufladę...');

      const result = await fiscalPrinterService.openDrawer();

      if (result.success) {
        addLog('Szuflada otwarta', 'success');
        toast.success('Szuflada kasowa została otwarta');
      } else {
        addLog(`Błąd: ${result.error}`, 'error');
        toast.error(`Błąd: ${result.error}`);
      }
    } catch (err) {
      addLog(`Błąd: ${err.message}`, 'error');
      toast.error(`Błąd: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Raport dobowy
  const printDailyReport = async () => {
    if (!window.confirm('Czy na pewno chcesz wykonać raport dobowy (Z)? Ta operacja zamknie dzień fiskalny.')) {
      return;
    }

    try {
      setLoading(true);
      addLog('Drukuję raport dobowy...');

      const result = await fiscalPrinterService.printDailyReport();

      if (result.success) {
        addLog('Raport dobowy wydrukowany', 'success');
        toast.success('Raport dobowy został wydrukowany');
      } else {
        addLog(`Błąd: ${result.error}`, 'error');
        toast.error(`Błąd: ${result.error}`);
      }
    } catch (err) {
      addLog(`Błąd: ${err.message}`, 'error');
      toast.error(`Błąd: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Test druku paragonu
  const testPrintReceipt = async () => {
    if (!window.confirm('Czy chcesz wydrukować testowy paragon?')) {
      return;
    }

    try {
      setLoading(true);
      addLog('Drukuję testowy paragon...');

      // Prosty test - 1 produkt, okrągłe liczby
      const testReceipt = {
        items: [
          { name: 'Test', quantity: 1, price: 1.00, vat_rate: 23 },
        ],
        total: 1.00,
        payment_type: 'cash',
        cashier: ''
      };

      const result = await fiscalPrinterService.printReceipt(testReceipt);

      if (result.success) {
        addLog('Testowy paragon wydrukowany', 'success');
        toast.success('Testowy paragon został wydrukowany');
      } else {
        addLog(`Błąd: ${result.error}`, 'error');
        toast.error(`Błąd: ${result.error}`);
      }
    } catch (err) {
      addLog(`Błąd: ${err.message}`, 'error');
      toast.error(`Błąd: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'success': return 'var(--success)';
      case 'error': return 'var(--danger)';
      case 'warning': return 'var(--warning)';
      default: return 'var(--text-muted)';
    }
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <h4 style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
        🖨️ Drukarka Fiskalna Novitus Deon
      </h4>

      {/* Ostrzeżenie o przeglądarce */}
      {!printerStatus.supported && (
        <div style={{
          backgroundColor: 'var(--warning-light)',
          color: 'var(--warning)',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          border: '1px solid var(--warning)'
        }}>
          <strong>⚠️ Nieobsługiwana przeglądarka!</strong>
          <p style={{ margin: '0.5rem 0 0' }}>
            Web Serial API wymaga Chrome 89+ lub Edge 89+.
            Otwórz tę stronę w Chrome lub Edge, aby połączyć się z drukarką.
          </p>
        </div>
      )}

      {error && (
        <div style={{
          backgroundColor: 'var(--danger-light)',
          color: 'var(--danger)',
          padding: '0.75rem',
          borderRadius: '0.375rem',
          marginBottom: '1rem',
          border: '1px solid var(--danger-light)'
        }}>
          {error}
        </div>
      )}

      {/* Wybór protokołu */}
      <div style={{
        backgroundColor: "var(--card-bg)",
        padding: '1.5rem',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem'
      }}>
        <h5 style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>Protokół Komunikacji</h5>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Wybierz protokół komunikacji z drukarką. Upewnij się, że ustawienie w drukarce odpowiada wybranemu protokołowi.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => handleProtocolChange('escpos')}
            disabled={loading}
            style={{
              padding: '6px 12px',
              backgroundColor: protocol === 'escpos' ? 'var(--info)' : 'var(--card-bg)',
              color: protocol === 'escpos' ? '#fff' : 'var(--text-secondary)',
              border: protocol === 'escpos' ? '1px solid var(--info)' : '1px solid var(--border)',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: protocol === 'escpos' ? '600' : '500',
              transition: 'all 0.2s'
            }}
          >
            ESC/POS (Binary)
          </button>
          <button
            onClick={() => handleProtocolChange('xml')}
            disabled={loading}
            style={{
              padding: '6px 12px',
              backgroundColor: protocol === 'xml' ? 'var(--success)' : 'var(--card-bg)',
              color: protocol === 'xml' ? '#fff' : 'var(--text-secondary)',
              border: protocol === 'xml' ? '1px solid var(--success)' : '1px solid var(--border)',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: protocol === 'xml' ? '600' : '500',
              transition: 'all 0.2s'
            }}
          >
            XML
          </button>
        </div>
        <div style={{
          marginTop: '0.75rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: protocol === 'xml' ? 'var(--success-light)' : 'var(--info-light)',
          borderRadius: '0.25rem',
          fontSize: '0.85rem',
          color: protocol === 'xml' ? 'var(--success)' : 'var(--info)'
        }}>
          {protocol === 'xml'
            ? 'Tryb XML - Komendy wysyłane jako dokumenty XML. Wymagany do druku paragonów fiskalnych (Novitus Deon).'
            : 'Tryb ESC/POS - Komendy binarne zgodne z protokołem Novitus v1.13. UWAGA: NIE obsługuje paragonów fiskalnych - tylko dokumenty niefiskalne i raporty. Sprzedaż z paragonem wymaga trybu XML.'}
        </div>
      </div>

      {/* Status połączenia */}
      <div style={{
        backgroundColor: "var(--card-bg)",
        padding: '1.5rem',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem'
      }}>
        <h5 style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>Status Połączenia</h5>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: printerStatus.connected ? 'var(--success)' : 'var(--danger)'
          }} />
          <span style={{ fontSize: '1.1rem', fontWeight: '500' }}>
            {printerStatus.connected ? 'Połączona' : 'Niepołączona'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {!printerStatus.connected ? (
            <button
              onClick={connectPrinter}
              disabled={loading || !printerStatus.supported}
              style={{
                padding: '6px 12px',
                border: '1px solid var(--success)', color: 'var(--success)' ,
                fontSize: '12px',
                cursor: loading || !printerStatus.supported ? 'not-allowed' : 'pointer',
                opacity: loading || !printerStatus.supported ? 0.6 : 1
              }}
            >
              🔌 Połącz z drukarką
            </button>
          ) : (
            <>
              <button
                onClick={disconnectPrinter}
                disabled={loading}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--danger)', color: 'var(--danger)' ,
                  fontSize: '12px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                ⏏️ Rozłącz
              </button>
              <button
                onClick={testConnection}
                disabled={loading}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--info)', color: 'var(--info)' ,
                  fontSize: '12px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                🔍 Test połączenia
              </button>
            </>
          )}
        </div>
      </div>

      {/* Funkcje drukarki */}
      {printerStatus.connected && (
        <div style={{
          backgroundColor: "var(--card-bg)",
          padding: '1.5rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '1.5rem'
        }}>
          <h5 style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>Funkcje Drukarki</h5>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={openDrawer}
              disabled={loading}
              style={{
                padding: '6px 12px',
                border: '1px solid var(--text-muted)', color: 'var(--text-muted)' ,
                fontSize: '12px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              📦 Otwórz szufladę
            </button>

            <button
              onClick={testPrintReceipt}
              disabled={loading}
              style={{
                padding: '6px 12px',
                border: '1px solid var(--info)', color: 'var(--info)' ,
                fontSize: '12px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              🧪 Testowy paragon
            </button>

            <button
              onClick={printDailyReport}
              disabled={loading}
              style={{
                padding: '6px 12px',
                backgroundColor: 'var(--warning)',
                color: 'var(--text)',
                fontSize: '12px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              📊 Raport dobowy (Z)
            </button>
          </div>
        </div>
      )}

      {/* Instrukcja */}
      <div style={{
        backgroundColor: 'var(--info-light)',
        padding: '1.5rem',
        borderRadius: '0.5rem',
        marginBottom: '1.5rem',
        border: '1px solid var(--info-light)'
      }}>
        <h5 style={{ marginBottom: '1rem', color: 'var(--info)' }}>📋 Instrukcja obsługi</h5>
        <ol style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--text-secondary)' }}>
          <li>Podłącz drukarkę Novitus Deon kablem USB do komputera</li>
          <li>Upewnij się, że drukarka jest włączona</li>
          <li>Kliknij przycisk "Połącz z drukarką"</li>
          <li>Wybierz port USB drukarki z listy (zwykle oznaczony jako "USB Serial")</li>
          <li>Po połączeniu możesz używać funkcji drukarki</li>
        </ol>
        <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'var(--warning-light)', borderRadius: '0.25rem' }}>
          <strong>⚠️ Ważne:</strong> Ta funkcja działa tylko w Chrome lub Edge.
          Drukarka musi być podłączona bezpośrednio do tego komputera.
        </div>
      </div>

      {/* Logi */}
      <div style={{
        backgroundColor: "var(--card-bg)",
        padding: '1.5rem',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h5 style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>📜 Logi komunikacji</h5>

        <div style={{
          maxHeight: '200px',
          overflowY: 'auto',
          fontFamily: 'monospace',
          fontSize: '0.85rem',
          backgroundColor: 'var(--bg)',
          padding: '0.75rem',
          borderRadius: '0.25rem',
          border: '1px solid var(--border)'
        }}>
          {logs.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Brak logów...
            </div>
          ) : (
            logs.map((log, index) => (
              <div key={index} style={{ marginBottom: '0.25rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>[{log.time}]</span>{' '}
                <span style={{ color: getLogColor(log.type) }}>{log.message}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default FiscalPrinterTab;
